import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Calendar, Loader2, ArrowLeft, CheckCircle, XCircle, Lock } from "lucide-react";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const [resetComplete, setResetComplete] = useState(false);
  const { settings } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setVerifying(false);
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await axios.post(`${API}/auth/verify-reset-token`, { token });
      setTokenValid(true);
      setTokenEmail(response.data.email);
    } catch (error) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("Le password non corrispondono");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password-with-token`, { 
        token,
        new_password: newPassword 
      });
      setResetComplete(true);
      toast.success("Password aggiornata con successo!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel reset della password");
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
          <p className="text-slate-600">Verifica del link in corso...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 pb-20 sm:pb-8 bg-white">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex items-center gap-3">
            {settings.logo_base64 ? (
              <img src={settings.logo_base64} alt="Logo" className="h-12 w-auto object-contain" />
            ) : (
              <div className="h-12 w-12 bg-slate-900 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-white" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-bold text-slate-900">{settings.nome_azienda || "Gestione Ferie"}</h2>
              <p className="text-sm text-slate-500">Gestione Ferie e Permessi</p>
            </div>
          </div>

          {/* No token provided */}
          {!token && (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="h-20 w-20 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="h-10 w-10 text-red-600" />
                </div>
              </div>
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Link non valido</h1>
                <p className="text-slate-600">
                  Il link di reset non contiene un token valido.
                </p>
              </div>
              <Button
                onClick={() => navigate("/forgot-password")}
                className="w-full h-12 btn-theme"
                data-testid="request-new-link-btn"
              >
                Richiedi un nuovo link
              </Button>
            </div>
          )}

          {/* Invalid or expired token */}
          {token && !tokenValid && !resetComplete && (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="h-20 w-20 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="h-10 w-10 text-red-600" />
                </div>
              </div>
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Link Scaduto</h1>
                <p className="text-slate-600">
                  Il link di reset è scaduto o non è più valido. Richiedi un nuovo link per reimpostare la password.
                </p>
              </div>
              <Button
                onClick={() => navigate("/forgot-password")}
                className="w-full h-12 btn-theme"
                data-testid="request-new-link-btn"
              >
                Richiedi un nuovo link
              </Button>
            </div>
          )}

          {/* Valid token - show reset form */}
          {token && tokenValid && !resetComplete && (
            <>
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Nuova Password</h1>
                <p className="text-slate-600">
                  Inserisci la nuova password per <strong>{tokenEmail}</strong>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-slate-700">Nuova Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    data-testid="new-password-input"
                    className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-500">Minimo 6 caratteri</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password" className="text-slate-700">Conferma Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    data-testid="confirm-password-input"
                    className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-password-btn"
                  className="w-full h-12 btn-theme rounded-md btn-press font-medium gap-2"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Aggiornamento...</>
                  ) : (
                    <>
                      <Lock className="h-4 w-4" />
                      Aggiorna Password
                    </>
                  )}
                </Button>
              </form>
            </>
          )}

          {/* Reset complete */}
          {resetComplete && (
            <div className="space-y-6">
              <div className="flex items-center justify-center">
                <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-10 w-10 text-emerald-600" />
                </div>
              </div>
              <div className="text-center space-y-2">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">Password Aggiornata!</h1>
                <p className="text-slate-600">
                  La tua password è stata aggiornata con successo. Ora puoi accedere con la nuova password.
                </p>
              </div>
              <Button
                onClick={() => navigate("/login")}
                className="w-full h-12 btn-theme"
                data-testid="go-to-login-btn"
              >
                Vai al Login
              </Button>
            </div>
          )}

          <div className="pt-4">
            <Link 
              to="/login" 
              className="inline-flex items-center text-slate-600 hover:text-slate-900"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Torna al Login
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://customer-assets.emergentagent.com/job_aea9fcf4-7192-4048-99b8-cf6b21f11caa/artifacts/1flrp8z8_Team.png"
          alt="Team working together"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/20"></div>
      </div>
    </div>
  );
}
